import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { useLocation } from 'react-router-dom';
import { ThreeDots } from 'react-loader-spinner';
import { apiCall } from '../utils/apiUtils';
import '../css/App.css';
import './ProfilePage.css';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { format, subDays, subWeeks, subMonths, startOfWeek, startOfMonth } from 'date-fns';
import { getUserEmail } from './auth/authConfig';
import {
    getStoredSessionId as getAnonymousSessionId,
    clearSession as clearAnonymousSession,
} from '../services/anonymousSessionService';
import { fetchWallet, fetchPacks, startCheckout } from '../services/billingService';
import { useAuth } from './authProvider/AuthContext';

const FEATURE_DISPLAY_NAMES = {
    blog_generation: 'Blog generation',
    blog_improve: 'Blog improve / rewrite',
};

function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
}

function formatMemberSince(value) {
    if (!value) return null;
    const date = typeof value === 'number' ? new Date(value * 1000) : new Date(value);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
}


const ProfilePage = ({ onBlogSelect, refreshSidebar }) => {
    const { accounts } = useMsal();
    const { user, loading: authLoading } = useAuth();
    const location = useLocation();
    const fileInputRef = useRef(null);

    // Stable account identifier — avoids re-firing effects on every render
    // since useMsal() returns a new array reference each time.
    const activeAccountId = accounts[0]?.homeAccountId ?? null;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('profile');
    const [profile, setProfile] = useState({
        displayName: '',
        email: '',
        subscriptionPlan: 'free',
        profilePicture: '',
        usageTracking: { blogCount: 0, lastUpdated: '' }
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Usage tab state
    const [usagePeriod, setUsagePeriod] = useState('daily');
    const [blogUsageData, setBlogUsageData] = useState([]);
    const [loadingUsage, setLoadingUsage] = useState(false);

    // Credits tab state
    const [wallet, setWallet] = useState(null);
    const [packs, setPacks] = useState([]);
    const [loadingCredits, setLoadingCredits] = useState(false);
    const [buyingPack, setBuyingPack] = useState(null);

    // Blog management state
    const [blogs, setBlogs] = useState([]);
    const [loadingBlogs, setLoadingBlogs] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOrder, setSortOrder] = useState('desc');
    const [editingBlog, setEditingBlog] = useState(null);

    // Handle URL parameters for direct navigation to a specific tab
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const tab = urlParams.get('tab');
        if (tab === 'blogs') setActiveTab('blogs');
        if (tab === 'credits') setActiveTab('credits');
        if (tab === 'usage') setActiveTab('usage');
    }, [location.search]);

    // Pre-populate profile state from AuthContext immediately so the page
    // renders with real data before the dedicated profile fetch completes.
    // This eliminates the blank / error flash when navigating directly to /profile.
    useEffect(() => {
        if (!user?.email) return;
        setProfile(prev => ({
            ...prev,
            id: user.id || prev.id,
            email: user.email || prev.email,
            displayName: user.displayName || prev.displayName,
            profilePicture: user.profilePicture !== undefined ? user.profilePicture : prev.profilePicture,
            subscriptionPlan: user.subscriptionPlan || prev.subscriptionPlan,
            usageTracking: user.usageTracking || prev.usageTracking,
            billing: user.billing || prev.billing,
            createdAt: user.createdAt || prev.createdAt,
            _ts: user._ts || prev._ts,
        }));
    }, [user]);

    // Fetch or create the full profile document.
    // Waits for AuthContext (MSAL bootstrap) to finish so that the token and
    // apiCall are properly initialised before making requests — this fixes the
    // "Failed to load profile" error that appeared on direct URL loads.
    useEffect(() => {
        if (authLoading) return;

        const fetchOrCreateProfile = async () => {
            const userEmail = getUserEmail(accounts && accounts[0]);
            if (!userEmail) {
                setError('No user account found');
                setLoading(false);
                return;
            }

            const buildNewProfile = () => {
                const anonymousSessionId = getAnonymousSessionId();
                const payload = {
                    id: userEmail,
                    email: userEmail,
                    displayName: accounts[0].name || userEmail,
                    subscriptionPlan: 'free',
                    profilePicture: '',
                    type: 'profile',
                    usageTracking: { blogCount: 0, lastUpdated: '' }
                };
                if (anonymousSessionId) payload.anonymousSessionId = anonymousSessionId;
                return payload;
            };

            try {
                const response = await apiCall('/get-profile-by-email', null, {
                    method: 'GET',
                    params: { email: userEmail }
                });

                if (response) {
                    setProfile(response);
                } else {
                    const createResponse = await apiCall('/add-profile', buildNewProfile());
                    setProfile(createResponse);
                    setSuccess('Profile created successfully');
                }
                clearAnonymousSession();
            } catch (err) {
                // apiCall surfaces the status code directly on the error object
                // (err.status), not on err.response — the previous check against
                // err.response.status was the root cause of the "Failed to load
                // profile" error shown on direct /profile loads for new users.
                if (err.status === 404) {
                    try {
                        const createResponse = await apiCall('/add-profile', buildNewProfile());
                        setProfile(createResponse);
                        setSuccess('Profile created successfully');
                        clearAnonymousSession();
                    } catch (createErr) {
                        setError('Failed to create profile');
                    }
                } else {
                    setError('Failed to load profile');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchOrCreateProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeAccountId, authLoading]);

    // Eagerly load wallet balance for the profile stats bar so it is visible
    // without the user needing to visit the Credits tab first.
    useEffect(() => {
        const loadWallet = async () => {
            try {
                const walletData = await fetchWallet();
                setWallet(walletData);
            } catch {
                // ignore — wallet badge handles error states globally
            }
        };
        loadWallet();
    }, []);

    const fetchBlogUsageData = useCallback(async () => {
        if (!getUserEmail(accounts && accounts[0])) return;
        setLoadingUsage(true);
        try {
            const response = await apiCall('/get-blog-usage-timeseries', null, {
                method: 'GET',
                params: { period: usagePeriod }
            });
            if (usagePeriod === 'daily') {
                const today = new Date();
                const last7Days = Array.from({ length: 7 }, (_, i) => {
                    const d = subDays(today, 6 - i);
                    return { period: format(d, 'MMM d'), raw: format(d, 'yyyy-MM-dd'), count: 0 };
                });
                const apiDataMap = {};
                if (Array.isArray(response)) {
                    response.forEach(item => { apiDataMap[item.period] = item.count; });
                }
                setBlogUsageData(last7Days.map(day => ({ period: day.period, count: apiDataMap[day.raw] || 0 })));
            } else if (usagePeriod === 'weekly') {
                const today = new Date();
                const last7Weeks = Array.from({ length: 7 }, (_, i) => {
                    const weekStart = startOfWeek(subWeeks(today, 6 - i), { weekStartsOn: 1 });
                    const isoYear = format(weekStart, 'RRRR');
                    const weekNum = format(weekStart, 'I');
                    return {
                        period: `${isoYear}-W${weekNum.padStart(2, '0')}`,
                        raw: `${isoYear}-W${weekNum.padStart(2, '0')}`,
                        count: 0
                    };
                });
                const apiDataMap = {};
                if (Array.isArray(response)) {
                    response.forEach(item => { apiDataMap[item.period] = item.count; });
                }
                setBlogUsageData(last7Weeks.map(week => ({ period: week.period, count: apiDataMap[week.raw] || 0 })));
            } else if (usagePeriod === 'monthly') {
                const today = new Date();
                const last7Months = Array.from({ length: 7 }, (_, i) => {
                    const monthStart = startOfMonth(subMonths(today, 6 - i));
                    return { period: format(monthStart, 'MMM yyyy'), raw: format(monthStart, 'yyyy-MM'), count: 0 };
                });
                const apiDataMap = {};
                if (Array.isArray(response)) {
                    response.forEach(item => { apiDataMap[item.period] = item.count; });
                }
                setBlogUsageData(last7Months.map(month => ({ period: month.period, count: apiDataMap[month.raw] || 0 })));
            } else {
                setBlogUsageData(Array.isArray(response) ? response : []);
            }
        } catch {
            setBlogUsageData([]);
        } finally {
            setLoadingUsage(false);
        }
    }, [accounts, usagePeriod]);

    useEffect(() => {
        if (activeTab === 'usage') fetchBlogUsageData();
    }, [activeTab, fetchBlogUsageData]);

    const loadCreditsData = useCallback(async () => {
        setLoadingCredits(true);
        try {
            const [walletData, packsData] = await Promise.all([fetchWallet(), fetchPacks()]);
            setWallet(walletData);
            setPacks(Array.isArray(packsData) ? packsData : []);
        } catch {
            // Silently fail — wallet badge handles the error state
        } finally {
            setLoadingCredits(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'credits') loadCreditsData();
    }, [activeTab, loadCreditsData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setProfile(prev => ({ ...prev, [name]: value }));
    };

    const handleAvatarClick = () => {
        fileInputRef.current?.click();
    };

    const handleAvatarChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setError('Please select a valid image file.');
            return;
        }
        if (file.size > 1024 * 1024) {
            setError('Image must be smaller than 1 MB.');
            return;
        }
        const reader = new FileReader();
        reader.onload = (evt) => {
            setProfile(prev => ({ ...prev, profilePicture: evt.target.result }));
        };
        reader.readAsDataURL(file);
        // Reset so the same file can be re-selected after clearing
        e.target.value = '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const response = await apiCall('/update-profile', {
                ...profile,
                id: profile.id || getUserEmail(accounts[0])
            }, { method: 'PUT' });
            setSuccess('Profile updated successfully');
            setProfile(response);
        } catch (err) {
            setError('Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    const handleBuyPack = async (packId) => {
        setBuyingPack(packId);
        try {
            await startCheckout(packId);
        } catch (err) {
            console.error('Checkout error:', err);
        } finally {
            setBuyingPack(null);
        }
    };

    const loadBlogs = async (page = 1, search = '', sort = 'desc') => {
        setLoadingBlogs(true);
        try {
            const response = await apiCall('/get-user-blogs', null, {
                method: 'GET',
                params: { page, page_size: 10, search, sort_by: 'createdAt', sort_order: sort }
            });
            if (response && response.blogs) {
                setBlogs(response.blogs);
                setTotalPages(response.pagination.total_pages);
                setCurrentPage(response.pagination.page);
            }
        } catch (err) {
            console.error('Error loading blogs:', err);
            setError('Failed to load blogs');
        } finally {
            setLoadingBlogs(false);
        }
    };

    const handleDeleteBlog = async (blogId) => {
        if (!window.confirm('Are you sure you want to delete this blog?')) return;
        try {
            await apiCall(`/delete-blog?id=${blogId}`, null, { method: 'DELETE' });
            setSuccess('Blog deleted successfully');
            loadBlogs(currentPage, searchQuery, sortOrder);
            if (refreshSidebar) refreshSidebar();
        } catch (err) {
            console.error('Error deleting blog:', err);
            setError('Failed to delete blog');
        }
    };

    const handleEditBlog = (blog) => {
        if (onBlogSelect) {
            onBlogSelect(blog);
        } else {
            setEditingBlog({ ...blog });
        }
    };

    const handleSaveEdit = async () => {
        try {
            const response = await apiCall('/update-blog', JSON.stringify({
                id: editingBlog.id,
                h1Title: editingBlog.h1Title,
                content: editingBlog.content,
                isStarred: editingBlog.isStarred
            }), { method: 'PUT', headers: { 'Content-Type': 'application/json' } });
            if (response) {
                setSuccess('Blog updated successfully');
                setEditingBlog(null);
                loadBlogs(currentPage, searchQuery, sortOrder);
                if (refreshSidebar) refreshSidebar();
            }
        } catch (err) {
            console.error('Error updating blog:', err);
            setError('Failed to update blog');
        }
    };

    const handleToggleStar = async (blog) => {
        try {
            const response = await apiCall(`/toggle-blog-star?id=${blog.id}`, null, { method: 'POST' });
            if (response) {
                loadBlogs(currentPage, searchQuery, sortOrder);
                if (refreshSidebar) refreshSidebar();
            }
        } catch (err) {
            console.error('Error toggling star:', err);
            setError('Failed to update blog');
        }
    };

    const handleSearchChange = (query) => {
        setSearchQuery(query);
        setCurrentPage(1);
        loadBlogs(1, query, sortOrder);
    };

    const handleSortChange = (order) => {
        setSortOrder(order);
        loadBlogs(currentPage, searchQuery, order);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    useEffect(() => {
        if (activeTab === 'blogs') loadBlogs();
    }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Render helpers ───────────────────────────────────────────────────────

    const renderAlerts = () => (
        <>
            {error && <div className="pp-alert pp-alert--error">{error}</div>}
            {success && <div className="pp-alert pp-alert--success">{success}</div>}
        </>
    );

    const renderProfileTab = () => {
        const displayName = profile.displayName || accounts[0]?.name || '';
        const email = profile.email || getUserEmail(accounts[0]) || '';
        const memberSince = formatMemberSince(profile.createdAt || profile._ts);
        const lastActive = profile.usageTracking?.lastUpdated
            ? new Date(profile.usageTracking.lastUpdated).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric'
              })
            : null;

        return (
            <div className="tab-content tab-content--fade">
                {renderAlerts()}

                {/* ── Hero ── */}
                <div className="pp-profile-hero">
                    <div
                        className="pp-avatar pp-avatar--clickable"
                        onClick={handleAvatarClick}
                        onKeyDown={(e) => e.key === 'Enter' && handleAvatarClick()}
                        role="button"
                        tabIndex={0}
                        title="Change profile picture"
                    >
                        {profile.profilePicture
                            ? <img src={profile.profilePicture} alt={displayName} className="pp-avatar__img" />
                            : getInitials(displayName)
                        }
                        <div className="pp-avatar__overlay" aria-hidden="true">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                                <circle cx="12" cy="13" r="4"/>
                            </svg>
                        </div>
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleAvatarChange}
                    />

                    <div className="pp-profile-hero__info">
                        <div className="pp-profile-hero__name-row">
                            <h2 className="pp-profile-hero__name">{displayName || 'Your Profile'}</h2>
                        </div>
                        <p className="pp-profile-hero__email">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                                <polyline points="22,6 12,13 2,6"/>
                            </svg>
                            {email}
                        </p>
                        <div className="pp-profile-hero__meta">
                            {memberSince && (
                                <span className="pp-meta-chip">
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                        <line x1="16" y1="2" x2="16" y2="6"/>
                                        <line x1="8" y1="2" x2="8" y2="6"/>
                                        <line x1="3" y1="10" x2="21" y2="10"/>
                                    </svg>
                                    Member since {memberSince}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Stats bar ── */}
                <div className="pp-stats-bar">
                    <div className="pp-stat-card">
                        <span className="pp-stat-card__icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                                <line x1="1" y1="10" x2="23" y2="10"/>
                            </svg>
                        </span>
                        <span className="pp-stat-card__value">
                            {wallet?.availableCredits ?? '—'}
                        </span>
                        <span className="pp-stat-card__label">Credits</span>
                    </div>
                    <div className="pp-stat-card">
                        <span className="pp-stat-card__icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14 2 14 8 20 8"/>
                                <line x1="16" y1="13" x2="8" y2="13"/>
                                <line x1="16" y1="17" x2="8" y2="17"/>
                            </svg>
                        </span>
                        <span className="pp-stat-card__value">{profile.usageTracking?.blogCount ?? 0}</span>
                        <span className="pp-stat-card__label">Blogs Created</span>
                    </div>
                    <div className="pp-stat-card">
                        <span className="pp-stat-card__icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/>
                                <polyline points="12 6 12 12 16 14"/>
                            </svg>
                        </span>
                        <span className="pp-stat-card__value pp-stat-card__value--sm">{lastActive || '—'}</span>
                        <span className="pp-stat-card__label">Last Active</span>
                    </div>
                </div>

                {/* ── Personal info form ── */}
                <div className="pp-form-card">
                    <h3 className="pp-form-card__title">Personal Information</h3>
                    <form onSubmit={handleSubmit} className="profile-form pp-form">
                        <div className="form-group">
                            <label htmlFor="displayName">Display Name</label>
                            <input
                                type="text"
                                id="displayName"
                                name="displayName"
                                value={profile.displayName}
                                onChange={handleInputChange}
                                required
                                placeholder="Your name"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">
                                Email
                                <span className="pp-readonly-badge">
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                                    </svg>
                                    Read only
                                </span>
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={email}
                                disabled
                                className="pp-input--readonly"
                            />
                            <p className="pp-field-hint">Managed by your sign-in provider and cannot be changed here.</p>
                        </div>

                        <button type="submit" className="submit-button pp-save-btn" disabled={saving}>
                            {saving
                                ? <span className="pp-save-btn__spinner"><ThreeDots color="#fff" height={20} width={44} /></span>
                                : 'Save Changes'
                            }
                        </button>
                    </form>
                </div>
            </div>
        );
    };

    const renderCreditsTab = () => {
        if (loadingCredits) {
            return (
                <div className="tab-content tab-content--fade">
                    <div className="loader-container">
                        <ThreeDots color="#0056b3" height={40} width={40} />
                    </div>
                </div>
            );
        }

        const available = wallet?.availableCredits ?? 0;
        const lifetimePurchased = wallet?.lifetimeCreditsPurchased ?? 0;
        const lifetimeUsed = wallet?.lifetimeCreditsUsed ?? 0;
        const trialClaimed = wallet?.trialCreditsClaimed ?? false;
        const trialCredits = wallet?.trialCredits ?? 0;
        const creditCosts = wallet?.creditCosts ?? {};
        const blogCost = creditCosts.blog_generation ?? 5;

        const renderPackCard = (packId, name, credits, price, isFeatured = false) => {
            const blogsFromPack = Math.floor(credits / blogCost);
            const priceLabel = price != null ? `$${price}` : null;
            const isBuying = buyingPack === packId;
            return (
                <div key={packId} className={`pp-credits-pack-card${isFeatured ? ' pp-credits-pack-card--featured' : ''}`}>
                    <div className="pp-credits-pack-card__name">{name}</div>
                    <div className="pp-credits-pack-card__credits">{credits} credits</div>
                    <div className="pp-credits-pack-card__value-hint">
                        = {blogsFromPack} blog generation{blogsFromPack !== 1 ? 's' : ''}
                    </div>
                    <div className="pp-credits-pack-card__reassurance">Credits never expire</div>
                    <button
                        className="pp-credits-pack-card__btn"
                        onClick={() => handleBuyPack(packId)}
                        disabled={isBuying}
                    >
                        {isBuying ? 'Loading...' : (priceLabel ? `Buy for ${priceLabel}` : 'Buy now')}
                    </button>
                    <div className="pp-credits-pack-card__trust">
                        <span className="pp-credits-pack-card__trust-icon">&#128274;</span> Secure checkout via Stripe
                    </div>
                </div>
            );
        };

        return (
            <div className="tab-content tab-content--fade">
                <h2 className="pp-section-title">Credits</h2>

                <div className="pp-credits-balance-card">
                    <div className="pp-credits-balance-card__left">
                        <span className="pp-credits-balance-card__label">Available Credits</span>
                        <span className={`pp-credits-balance-card__amount ${available <= 10 ? 'pp-credits-balance-card__amount--low' : ''}`}>
                            {available}
                        </span>
                        {available <= 10 && available > 0 && (
                            <span className="pp-credits-low-badge">Running low</span>
                        )}
                        {available === 0 && (
                            <span className="pp-credits-empty-badge">No credits remaining</span>
                        )}
                    </div>
                    <div className="pp-credits-balance-card__right">
                        <div className="pp-credits-stat">
                            <span className="pp-credits-stat__value">{lifetimePurchased}</span>
                            <span className="pp-credits-stat__label">Total purchased</span>
                        </div>
                        <div className="pp-credits-stat">
                            <span className="pp-credits-stat__value">{lifetimeUsed}</span>
                            <span className="pp-credits-stat__label">Total used</span>
                        </div>
                        {trialCredits > 0 && (
                            <div className="pp-credits-stat">
                                <span className={`pp-credits-trial-badge ${trialClaimed ? 'pp-credits-trial-badge--claimed' : 'pp-credits-trial-badge--available'}`}>
                                    {trialClaimed ? `${trialCredits} trial credits used` : `${trialCredits} trial credits available`}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {available === 0 && (
                    <div className="pp-credits-urgency-banner pp-credits-urgency-banner--empty">
                        You have no credits. Top up below to generate your next blog.
                    </div>
                )}
                {available > 0 && available <= 10 && (
                    <div className="pp-credits-urgency-banner pp-credits-urgency-banner--low">
                        You are running low. Top up to keep publishing without interruption.
                    </div>
                )}

                <div className="pp-credits-packs">
                    <h3 className="pp-credits-packs__title">Keep publishing</h3>
                    <div className="pp-credits-pack-grid">
                        {packs.length > 0 ? (
                            packs.map((pack) =>
                                renderPackCard(
                                    pack.id || pack.packId,
                                    pack.name || pack.label || 'Credits Pack',
                                    pack.credits,
                                    pack.price ?? pack.priceUsd ?? pack.amountUsd ?? null,
                                    true
                                )
                            )
                        ) : (
                            renderPackCard('launch_pack', 'Launch Pack', 150, 10, true)
                        )}
                    </div>
                </div>

                <div className="pp-credits-costs">
                    <h3 className="pp-credits-costs__title">Credit costs</h3>
                    <ul className="pp-credits-cost-list">
                        {Object.keys(creditCosts).length > 0 ? (
                            Object.entries(creditCosts).map(([feature, cost]) => (
                                <li key={feature} className="pp-credits-cost-item">
                                    <span className="pp-credits-cost-item__name">
                                        {FEATURE_DISPLAY_NAMES[feature] || feature.replace(/_/g, ' ')}
                                    </span>
                                    <span className="pp-credits-cost-item__cost">
                                        {cost} {cost === 1 ? 'credit' : 'credits'}
                                    </span>
                                </li>
                            ))
                        ) : (
                            <>
                                <li className="pp-credits-cost-item">
                                    <span className="pp-credits-cost-item__name">Blog generation</span>
                                    <span className="pp-credits-cost-item__cost">5 credits</span>
                                </li>
                                <li className="pp-credits-cost-item">
                                    <span className="pp-credits-cost-item__name">Blog improve</span>
                                    <span className="pp-credits-cost-item__cost">3 credits</span>
                                </li>
                            </>
                        )}
                    </ul>
                    <p className="pp-credits-note">
                        Trends and headlines are always included. Credits are only charged when a blog is generated or improved.
                    </p>
                </div>
            </div>
        );
    };

    const renderUsageTab = () => {
        const totalBlogs = profile.usageTracking?.blogCount ?? 0;

        return (
            <div className="tab-content tab-content--fade">
                <h2 className="pp-section-title">Usage</h2>

                <div className="pp-usage-stats-row">
                    <div className="pp-usage-stat-card">
                        <span className="pp-usage-stat-card__value">{totalBlogs}</span>
                        <span className="pp-usage-stat-card__label">Blogs generated (all time)</span>
                    </div>
                </div>

                <div className="pp-usage-chart-section">
                    <div className="pp-usage-chart-header">
                        <h3 className="pp-usage-chart-title">Blogs Generated</h3>
                        <select
                            value={usagePeriod}
                            onChange={(e) => setUsagePeriod(e.target.value)}
                            className="period-select"
                        >
                            <option value="daily">Daily (last 7 days)</option>
                            <option value="weekly">Weekly (last 7 weeks)</option>
                            <option value="monthly">Monthly (last 7 months)</option>
                        </select>
                    </div>

                    {loadingUsage ? (
                        <div className="loader-container" style={{ minHeight: 270 }}>
                            <ThreeDots color="#0056b3" height={40} width={40} />
                        </div>
                    ) : blogUsageData.length === 0 || blogUsageData.every(d => d.count === 0) ? (
                        <div className="pp-no-usage-data" style={{ minHeight: 270, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '8px', color: '#6b7280' }}>
                            <span style={{ fontSize: '2rem' }}>📊</span>
                            <p style={{ margin: 0, fontWeight: 500 }}>No blog generation history for this period.</p>
                            {(profile.usageTracking?.blogCount ?? 0) > 0 && (
                                <p style={{ margin: 0, fontSize: '0.85rem' }}>
                                    You have generated {profile.usageTracking.blogCount} blog{profile.usageTracking.blogCount !== 1 ? 's' : ''} in total —
                                    per-day history is tracked from the date you first used this feature.
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className="usage-stats">
                            <ResponsiveContainer width="100%" height={270}>
                                <BarChart data={blogUsageData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="count" name="Blogs Generated" fill="#0056b3" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderBlogsTab = () => (
        <div className="tab-content tab-content--fade">
            <h2 className="pp-section-title">Blog Management</h2>

            <div className="blogs-header">
                <div className="blogs-controls">
                    <input
                        type="text"
                        placeholder="Search blogs..."
                        value={searchQuery}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        className="blog-search-input"
                    />
                    <select
                        value={sortOrder}
                        onChange={(e) => handleSortChange(e.target.value)}
                        className="sort-select"
                    >
                        <option value="desc">Newest First</option>
                        <option value="asc">Oldest First</option>
                    </select>
                </div>
            </div>

            {loadingBlogs ? (
                <div className="loader-container">
                    <ThreeDots color="#00BFFF" height={40} width={40} />
                </div>
            ) : (
                <>
                    <div className="blogs-table">
                        {blogs.length > 0 ? (
                            <table>
                                <thead>
                                    <tr>
                                        <th>Starred</th>
                                        <th>Title</th>
                                        <th>Created</th>
                                        <th>Updated</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {blogs.map((blog) => (
                                        <tr key={blog.id}>
                                            <td className="starred-column">
                                                <button
                                                    onClick={() => handleToggleStar(blog)}
                                                    className={`star-toggle-button ${blog.isStarred ? 'starred' : ''}`}
                                                    title={blog.isStarred ? 'Remove from starred' : 'Add to starred'}
                                                >
                                                    {blog.isStarred ? '⭐' : '☆'}
                                                </button>
                                            </td>
                                            <td className="title-column">{blog.h1Title}</td>
                                            <td>{formatDate(blog.createdAt)}</td>
                                            <td>{formatDate(blog.updatedAt)}</td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button
                                                        onClick={() => handleEditBlog(blog)}
                                                        className="edit-button"
                                                        title="Edit blog"
                                                    >
                                                        ✏️
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteBlog(blog.id)}
                                                        className="delete-button"
                                                        title="Delete blog"
                                                    >
                                                        🗑️
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="pp-blogs-empty">
                                <div className="pp-blogs-empty__icon">
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                        <polyline points="14 2 14 8 20 8"/>
                                        <line x1="16" y1="13" x2="8" y2="13"/>
                                        <line x1="16" y1="17" x2="8" y2="17"/>
                                    </svg>
                                </div>
                                <p className="pp-blogs-empty__title">
                                    {searchQuery ? 'No blogs match your search' : 'No blogs yet'}
                                </p>
                                <p className="pp-blogs-empty__sub">
                                    {searchQuery
                                        ? 'Try a different search term.'
                                        : 'Blogs you generate will appear here.'}
                                </p>
                            </div>
                        )}
                    </div>

                    {totalPages > 1 && (
                        <div className="pagination">
                            <button
                                onClick={() => loadBlogs(currentPage - 1, searchQuery, sortOrder)}
                                disabled={currentPage === 1}
                                className="pagination-button"
                            >
                                Previous
                            </button>
                            <span className="pagination-info">Page {currentPage} of {totalPages}</span>
                            <button
                                onClick={() => loadBlogs(currentPage + 1, searchQuery, sortOrder)}
                                disabled={currentPage === totalPages}
                                className="pagination-button"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    const renderTabContent = () => {
        switch (activeTab) {
            case 'profile': return renderProfileTab();
            case 'blogs':   return renderBlogsTab();
            case 'credits': return renderCreditsTab();
            case 'usage':   return renderUsageTab();
            default:        return null;
        }
    };

    // During sign-out the MSAL accounts array is cleared before the redirect
    // to the IdP end_session endpoint actually navigates away.
    if (!accounts || accounts.length === 0) return null;

    // Show a full-page loader while MSAL is initialising OR while we have no
    // pre-populated user data to render yet.
    const showLoader = authLoading || (loading && !user?.email);
    if (showLoader) {
        return (
            <div className="loader-container">
                <ThreeDots color="#00BFFF" height={80} width={80} />
            </div>
        );
    }

    const TABS = [
        {
            key: 'profile',
            label: 'Profile',
            icon: (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                </svg>
            )
        },
        {
            key: 'blogs',
            label: 'My Blogs',
            icon: (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
            )
        },
        {
            key: 'credits',
            label: 'Credits',
            icon: (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                    <line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
            )
        },
        {
            key: 'usage',
            label: 'Usage',
            icon: (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="20" x2="18" y2="10"/>
                    <line x1="12" y1="20" x2="12" y2="4"/>
                    <line x1="6" y1="20" x2="6" y2="14"/>
                </svg>
            )
        },
    ];

    return (
        <div className="profile-container">
            <div className="pp-tabs">
                {TABS.map(({ key, label, icon }) => (
                    <button
                        key={key}
                        className={`pp-tab-btn ${activeTab === key ? 'pp-tab-btn--active' : ''}`}
                        onClick={() => setActiveTab(key)}
                    >
                        <span className="pp-tab-btn__icon" aria-hidden="true">{icon}</span>
                        {label}
                    </button>
                ))}
            </div>

            {renderTabContent()}

            {editingBlog && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Edit Blog</h3>
                        <div className="form-group">
                            <label>Title</label>
                            <input
                                type="text"
                                value={editingBlog.h1Title}
                                onChange={(e) => setEditingBlog({ ...editingBlog, h1Title: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label>Content</label>
                            <textarea
                                value={editingBlog.content}
                                onChange={(e) => setEditingBlog({ ...editingBlog, content: e.target.value })}
                                rows={10}
                            />
                        </div>
                        <div className="modal-actions">
                            <button onClick={handleSaveEdit} className="save-button">Save Changes</button>
                            <button onClick={() => setEditingBlog(null)} className="cancel-button">Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfilePage;
